"use client";

import { useRef, type InputHTMLAttributes } from "react";
import { cx } from "../cx.ts";
import { Icon } from "./icon.tsx";
import { IconButton } from "./icon-button.tsx";

type Size = "md" | "lg";

/**
 * `lg` is the till, and both values move together for the reason
 * `PasswordInput` gives: a terminal is typed on with a thumb, and a 32px
 * target under a finger is the defect a taller field would otherwise hide.
 */
const SIZES: Record<Size, { field: string; pad: string; button: "sm" | "md" }> = {
  md: { field: "h-10 text-sm", pad: "pe-11", button: "sm" },
  lg: { field: "h-12 text-base", pad: "pe-12", button: "md" },
};

/**
 * A clear button cannot exist without an accessible name, so the two arrive
 * together or not at all — the rule `IconButton` enforces by making `label`
 * required rather than optional. A *prop* rather than a bare `aria-label` for
 * the same reason it gives: a hardcoded English string at an app call site is
 * then caught by `untranslated-strings.test.ts`.
 */
type ClearProps =
  { onClear: () => void; clearLabel: string } | { onClear?: undefined; clearLabel?: undefined };

// `size` is dropped for the reason `PasswordInput` documents: the HTML
// attribute is a character width nothing here sets, and leaving it would
// intersect with the prop below to `never` rather than overriding it.
export type SearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> &
  ClearProps & { size?: Size };

/**
 * The search box every list page carries. Debouncing lives in `useListData`,
 * not here — a component that debounced its own value would fight the hook
 * that owns the query.
 *
 * `className` lands on the wrapper, which is what callers were sizing anyway;
 * the input is always full-width inside it. The padding that clears the icon
 * is logical (`ps-9`), so the icon moves to the right in a right-to-left
 * locale without a second rule.
 */
export function SearchInput({
  className,
  size = "md",
  onClear,
  clearLabel,
  ...rest
}: SearchInputProps) {
  const input = useRef<HTMLInputElement>(null);
  const { field, pad, button } = SIZES[size];
  const hasValue = String(rest.value ?? "").length > 0;

  return (
    <div className={cx("relative flex w-full items-center", className)}>
      <Icon
        name="search"
        size={16}
        className="pointer-events-none absolute start-3 text-ink-subtle"
      />
      <input
        ref={input}
        type="search"
        className={cx(
          "w-full rounded-control border border-line bg-surface ps-9",
          "text-ink-body placeholder:text-ink-subtle transition-colors duration-150",
          "hover:border-line-strong focus:border-primary focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-primary/20",
          field,
          // The trailing room is constant while typing, so the text does not
          // shift at the moment the button appears. WebKit draws its own ✕ and
          // ours would sit a few pixels from it — the `::-ms-reveal` defect
          // `PasswordInput` documents — so it is hidden only where we supply a
          // replacement. A caller without `onClear` keeps the native one.
          onClear === undefined ? "pe-3" : cx(pad, "[&::-webkit-search-cancel-button]:hidden"),
        )}
        {...rest}
      />
      {/* Absolutely positioned and still vertically centred: the wrapper's
          `items-center` governs an out-of-flow child whose block-axis insets
          are auto, which is why this holds at both heights. `PasswordInput`
          leans on the same rule. */}
      {onClear && clearLabel && hasValue ? (
        <IconButton
          icon="x"
          label={clearLabel}
          size={button}
          className="absolute end-1"
          onClick={() => {
            onClear();
            // Focus must not fall to `<body>` when the button it was on
            // unmounts, and clearing is exactly what unmounts it. Owning this
            // here fixes it once for every caller.
            input.current?.focus();
          }}
        />
      ) : null}
    </div>
  );
}
